import { useStore } from '@nanostores/react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { getApiClientErrorStatus } from '@/services/api';
import { getDsBrandDocumentGenerateStatusQueryOptions } from '@/services/db/ds-brand-document/queries';
import { getDsGlobalStylesProcessStatusQueryOptions } from '@/services/db/ds-global-styles/queries';
import {
  $brandWizardDraft,
  updateBrandWizardDraft,
  type BrandWizardDraft,
  type BrandWizardDraftIdentity,
  type BrandWizardRun,
} from '../wizard/draft-store';
import {
  AUTHORING_POLL_INTERVAL_MS,
  deriveBrandExtractState,
  deriveBrandRunState,
  EXTRACT_POLL_INTERVAL_MS,
  extractPhaseToPersistFor,
  phaseToPersistFor,
  terminalPhaseFor,
  type BrandExtractStatusInput,
  type BrandRunState,
  type BrandRunStatusInput,
} from './brand-run-state';

/** Per-source verdicts from the extract status poll, keyed by the submitted source id. */
export type ExtractSourceStatuses = Record<string, string>;

type AuthoringPoll = {
  status: BrandRunStatusInput;
  refetch: () => Promise<BrandRunState | null>;
};

type ExtractPoll = {
  status: BrandExtractStatusInput;
  sourceStatuses: ExtractSourceStatuses;
};

type Tracker = {
  draft: BrandWizardDraft | null;
  state: BrandRunState | null;
  /** Polls once now and resolves with the state that result derives to. */
  refetch: () => Promise<BrandRunState | null>;
  /** While extracting: what the backend says about each submitted source. */
  extractSourceStatuses: ExtractSourceStatuses;
};

/** Stands in for a run in a disabled query's key, so the options factory is never called with a partial. */
const NO_RUN: BrandWizardRun = { workflowId: '', runId: '' };

/** What one half of the run polls: its identity, its clock, and whether it should poll at all. */
type PollTarget = {
  run: BrandWizardRun | null;
  startedAt: number | null;
  shouldPoll: boolean;
};

function pollTarget(
  workspaceId: number | null,
  draft: BrandWizardDraft | null,
  livePhase: BrandWizardDraft['phase'],
  storedRun: BrandWizardRun | undefined,
): PollTarget {
  const run = storedRun ?? null;
  return {
    run,
    startedAt: draft?.startedAt ?? null,
    shouldPoll: draft?.phase === livePhase && run != null && workspaceId != null,
  };
}

/**
 * When the last result landed, success or error. The stamps, not just the
 * payload: an unchanged `in_progress` body comes back with the same object
 * identity but a newer stamp, and the stall window has to be re-read against
 * every result. Zero means nothing has landed yet.
 */
function lastPolledAt(query: { dataUpdatedAt: number; errorUpdatedAt: number }): number {
  return Math.max(query.dataUpdatedAt, query.errorUpdatedAt);
}

/** The backend's per-source verdicts, keyed by the id the caller submitted them under. */
function toSourceStatuses(sources: ReadonlyArray<{ id: string; status: string }> | null | undefined) {
  const merged: ExtractSourceStatuses = {};
  for (const source of sources ?? []) merged[source.id] = source.status;
  return merged;
}

/** Scopes this tracker's writes to the run it polls, so a record another tab replaced is left alone. */
function draftIdentity(workspaceId: number | null, run: BrandWizardRun | null): BrandWizardDraftIdentity {
  return { workspaceId: workspaceId ?? 0, authorRun: run ?? undefined };
}

/**
 * The EXTRACT half of a run: poll while the draft says `extracting`, persist
 * every outcome, and report the per-source verdicts the compact reading card
 * draws. Its own hook because it reads a different endpoint on its own clock;
 * splitting it keeps each half's query, effect and derivation together.
 */
function useExtractRunPoll(workspaceId: number | null, draft: BrandWizardDraft | null): ExtractPoll {
  const { run, startedAt, shouldPoll } = pollTarget(workspaceId, draft, 'extracting', draft?.extractRun);

  const query = useQuery({
    ...getDsGlobalStylesProcessStatusQueryOptions(workspaceId ?? 0, run ?? NO_RUN),
    enabled: shouldPoll,
    refetchInterval: shouldPoll ? EXTRACT_POLL_INTERVAL_MS : false,
    refetchIntervalInBackground: true,
    retry: false,
  });

  const { data, isError } = query;
  const errorStatus = isError ? (getApiClientErrorStatus(query.error) ?? null) : null;
  const polledAt = lastPolledAt(query);

  useEffect(
    // Reacts to poll results, so it must re-run per result — not mount-only.
    function recordExtractOutcome() {
      if (!shouldPoll || startedAt == null || polledAt === 0) return;
      const phase = extractPhaseToPersistFor({ data, isError, errorStatus }, startedAt, Date.now());
      if (phase != null) {
        updateBrandWizardDraft({ phase }, { workspaceId: workspaceId ?? 0, extractRun: run ?? undefined });
      }
    },
    [shouldPoll, startedAt, data, isError, errorStatus, polledAt, workspaceId, run],
  );

  const sourceStatuses = useMemo<ExtractSourceStatuses>(() => toSourceStatuses(data?.sources), [data]);

  return { status: { data, isError, errorStatus }, sourceStatuses };
}

/**
 * The AUTHORING half: poll while the draft says `authoring`, persist every
 * outcome (a stall included), and expose the one manual re-check the stalled
 * banner needs.
 */
function useAuthoringRunPoll(workspaceId: number | null, draft: BrandWizardDraft | null): AuthoringPoll {
  const { run, startedAt, shouldPoll } = pollTarget(workspaceId, draft, 'authoring', draft?.authorRun);

  const query = useQuery({
    ...getDsBrandDocumentGenerateStatusQueryOptions(workspaceId ?? 0, run ?? NO_RUN),
    enabled: shouldPoll,
    refetchInterval: shouldPoll ? AUTHORING_POLL_INTERVAL_MS : false,
    refetchIntervalInBackground: true,
    retry: false,
  });

  const { data, isError } = query;
  const errorStatus = isError ? (getApiClientErrorStatus(query.error) ?? null) : null;
  const polledAt = lastPolledAt(query);

  useEffect(
    // Reacts to poll results, so it must re-run per result — not mount-only.
    function recordPollOutcome() {
      if (!shouldPoll || startedAt == null || polledAt === 0) return;
      const phase = phaseToPersistFor({ data, isError, errorStatus }, startedAt, Date.now());
      if (phase != null) updateBrandWizardDraft({ phase }, draftIdentity(workspaceId, run));
    },
    [shouldPoll, startedAt, data, isError, errorStatus, polledAt, workspaceId, run],
  );

  /**
   * One manual status fetch, for "Check again" on a run that stopped answering.
   * TanStack still fetches a disabled query on an explicit refetch, which is
   * what lets a stalled run be re-checked without the background poll running.
   *
   * Resolves with the FRESH derivation rather than leaving the caller to read a
   * state it captured before the await, and persists it: the poll effect is off
   * while the draft is terminal, so this is the only writer for this result.
   */
  const refetch = async (): Promise<BrandRunState | null> => {
    if (draft == null) return null;
    const result = await query.refetch();
    const fresh: BrandRunStatusInput = {
      data: result.data,
      isError: result.isError,
      errorStatus: result.isError ? (getApiClientErrorStatus(result.error) ?? null) : null,
    };
    // Still not answering: leave the draft exactly as it was, so a stalled run
    // stays stalled rather than being revived on no evidence. An errored refetch
    // keeps the last successful payload on `result.data`, so check the error first.
    if (result.isError || result.data == null) return deriveBrandRunState(draft, fresh, Date.now());
    // The server just answered, so the stall clock is not part of this verdict:
    // an in-progress run is alive, and gets a fresh clock so the background
    // poll (and the wizard takeover) resume from now.
    const terminal = terminalPhaseFor(result.data);
    const phase = terminal ?? 'authoring';
    const startedAgainAt = terminal == null ? Date.now() : draft.startedAt;
    updateBrandWizardDraft({ phase, startedAt: startedAgainAt }, draftIdentity(workspaceId, run));
    return deriveBrandRunState({ ...draft, phase, startedAt: startedAgainAt }, fresh, startedAgainAt);
  };

  return { status: { data, isError, errorStatus }, refetch };
}

/** Whether this draft is in the extract half, so the extract poll is what describes it. */
function isExtractPhase(draft: BrandWizardDraft): boolean {
  return draft.phase === 'extracting' || draft.phase === 'extract_ready' || draft.phase === 'extract_failed';
}

/**
 * Follows the workspace's in-flight brand run from anywhere in the app — both
 * halves of it, reading the sources and writing the document. Polls the SAME
 * query keys the wizard uses, so however many surfaces mount (wizard takeover,
 * the pages' run banner, layout snackbar) there is one poll per half. Keeps
 * polling with the tab in the background so a run that finishes while the user
 * is elsewhere is already waiting for them when they come back.
 *
 * Each half only polls while the draft says it is running, and every outcome —
 * including a stall — is written back to the draft, so a run that stops
 * answering stops being polled instead of ticking on from every page forever.
 */
export function useBrandRunTracker(workspaceId: number | null): Tracker {
  const stored = useStore($brandWizardDraft);
  const draft = stored != null && workspaceId != null && stored.workspaceId === workspaceId ? stored : null;

  const authoring = useAuthoringRunPoll(workspaceId, draft);
  const extract = useExtractRunPoll(workspaceId, draft);

  // Derived per render rather than memoized: the stall window is a comparison
  // against the current clock, which a memo keyed on payload identity would
  // freeze for as long as the server keeps repeating the same body.
  const state =
    draft == null
      ? null
      : isExtractPhase(draft)
        ? deriveBrandExtractState(draft, extract.status, Date.now())
        : deriveBrandRunState(draft, authoring.status, Date.now());

  return { draft, state, refetch: authoring.refetch, extractSourceStatuses: extract.sourceStatuses };
}
